/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import {
  ButtonLabelLayoutStyles,
  ButtonLabelLayoutVariantOptions
} from '../../UNSAFE_ButtonLabelLayout/themes/ButtonLabelLayoutStyles.css';
import { ButtonLabelLayoutRedwoodTheme } from '../../UNSAFE_ButtonLabelLayout/themes/redwood/ButtonLabelLayoutTheme';
import { ComponentProps, ComponentChildren } from 'preact';
import { ButtonLabelLayout } from '../../UNSAFE_ButtonLabelLayout/ButtonLabelLayout';
import { useComponentTheme } from '#hooks/UNSAFE_useComponentTheme';
import { useButtonLabelLayoutTheme } from '#hooks/PRIVATE_useButtonLabelLayoutTheme';

export type ProgressLayoutInfo = {
  startIconClasses: string;
  labelClasses: string;
  labelStyles: Record<string, string>;
  progressIconClasses: string;
  containerClasses: string;
  isShowIcons: boolean;
  isShowText: boolean;
  hasTextStartIcon: boolean;
};

export type ButtonDisplay = 'icons' | 'label' | 'all';

export type ButtonLabelLayoutProps = {
  /**
   *The default slot is the button's text label. The oj-c-button element accepts plain text or DOM nodes as children for the default slot."
   */
  children?: ComponentChildren;

  /**
   * Display indicates whether the label, the icons, or all elements should be rendered by the button.
   */
  display?: ButtonDisplay;

  /**
   * The startIcon is the button's start icon.
   */
  startIcon?: ComponentChildren;

  /**
   * Styling properties
   */
  styling?: ComponentProps<typeof ButtonLabelLayout>['styling'];

  /**
   * The size indicates how large the button is rendered.
   */
  size?: ComponentProps<typeof ButtonLabelLayout>['size'];

  /**
   *The suffix is appended to the text label, used for menu buttons.
   */
  suffix?: string;

  /**
   * Specifies that a quick indeterminate action is taking place, and a progress circle should be rendered.
   */
  isLoading?: boolean;
};

export function useProgressButtonLabelLayoutTheme({
  display,
  styling,
  size,
  children,
  startIcon,
  isLoading = false
}: ButtonLabelLayoutProps): ProgressLayoutInfo {
  const { startIconClasses, containerClasses, isShowIcons, isShowText, hasTextStartIcon } =
    useButtonLabelLayoutTheme({ display, size, styling, children, startIcon });

  const { classes: labelClasses, styles: labelStyles } = useComponentTheme<
    ButtonLabelLayoutVariantOptions,
    ButtonLabelLayoutStyles
  >(ButtonLabelLayoutRedwoodTheme, {
    target: 'label',
    size: size,
    fill: styling == 'fill' ? 'isFill' : 'notFill',
    textEndIcon: 'notTextEndIcon',
    textStartIcon: hasTextStartIcon ? 'hasTextStartIcon' : 'notTextStartIcon',
    suffix: 'notSuffix',
    twoIcons: 'notTwoIcons',
    loading: isLoading && !hasTextStartIcon ? 'isLoading' : 'notLoading'
  });

  const { classes: progressIconClasses } = useComponentTheme<
    ButtonLabelLayoutVariantOptions,
    ButtonLabelLayoutStyles
  >(ButtonLabelLayoutRedwoodTheme, {
    target: 'progressIcon',
    size: size
  });

  return {
    startIconClasses,
    labelClasses,
    labelStyles,
    progressIconClasses,
    containerClasses,
    isShowIcons,
    isShowText,
    hasTextStartIcon
  };
}
